import { Fragment } from "react";
import { CaretRight, CaretDown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/* Brand tone map reused across all architecture diagrams. */
export const TONES = {
  orange: { tint: "bg-primary/12", text: "text-primary-strong", solid: "bg-primary", ring: "ring-primary/20", border: "border-primary/40", soft: "bg-primary/[0.06]" },
  blue: { tint: "bg-blue/12", text: "text-blue-ink", solid: "bg-blue", ring: "ring-blue/20", border: "border-blue/40", soft: "bg-blue/[0.06]" },
  teal: { tint: "bg-teal/15", text: "text-teal-ink", solid: "bg-teal", ring: "ring-teal/20", border: "border-teal/40", soft: "bg-teal/[0.06]" },
  yellow: { tint: "bg-secondary/25", text: "text-secondary-ink", solid: "bg-secondary", ring: "ring-secondary/30", border: "border-secondary/50", soft: "bg-secondary/[0.08]" },
  neutral: { tint: "bg-muted", text: "text-foreground", solid: "bg-muted-foreground", ring: "ring-border", border: "border-border", soft: "bg-muted/40" },
};

/* ------------------------------- Flow (journey) ---------------------------------- */
function FlowArrow() {
  return (
    <div className="flex items-center justify-center text-muted-foreground/70 md:px-0.5">
      <CaretDown size={20} weight="bold" className="md:hidden" />
      <CaretRight size={22} weight="bold" className="hidden md:block" />
    </div>
  );
}

export function Flow({ steps }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-stretch">
      {steps.map((s, i) => {
        const t = TONES[s.tone || "orange"];
        return (
          <Fragment key={s.label}>
            <div className="flex flex-1 flex-col items-center rounded-3xl border border-border bg-card p-5 text-center shadow-soft md:items-start md:text-left">
              <span className={cn("flex h-12 w-12 items-center justify-center rounded-2xl", t.tint, t.text)}>
                {s.icon && <s.icon size={24} weight="fill" />}
              </span>
              <p className="mt-3 font-display text-lg font-extrabold">{s.label}</p>
              {s.desc && <p className="mt-1 text-sm font-medium leading-relaxed text-muted-foreground">{s.desc}</p>}
            </div>
            {i < steps.length - 1 && <FlowArrow />}
          </Fragment>
        );
      })}
    </div>
  );
}

/* ----------------------------------- Area node ----------------------------------- */
export function AreaNode({ icon: Icon, label, purpose, items = [], tone = "orange", className }) {
  const t = TONES[tone];
  return (
    <div className={cn("rounded-3xl border-2 bg-card p-5 shadow-soft", t.border, className)}>
      <div className="flex items-center gap-3">
        <span className={cn("flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl", t.tint, t.text)}>
          {Icon && <Icon size={24} weight="fill" />}
        </span>
        <div>
          <p className="font-display text-lg font-extrabold leading-tight">{label}</p>
          {purpose && <p className="text-sm font-semibold text-muted-foreground">{purpose}</p>}
        </div>
      </div>
      {items.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {items.map((it) => (
            <span key={it} className={cn("rounded-full px-2.5 py-1 text-xs font-bold", t.tint, t.text)}>{it}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Comparison matrix ------------------------------- */
export function Matrix({ firstColLabel = "", columns = [], rows = [] }) {
  return (
    <div className="w-full overflow-x-auto rounded-3xl border border-border bg-card shadow-soft">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="bg-muted/40">
            <th className="p-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">{firstColLabel}</th>
            {columns.map((c) => (
              <th key={c} className="p-4 font-display font-extrabold">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.label} className={i > 0 ? "border-t border-border" : "border-t border-border"}>
              <td className="p-4 align-top font-bold">{r.label}</td>
              {r.values.map((v, j) => (
                <td key={j} className="p-4 align-top font-medium leading-relaxed text-muted-foreground">{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------- Stacked bars ---------------------------------- */
export function StackedBars({ data = [], segments = [] }) {
  return (
    <div>
      <div className="flex items-end gap-3 sm:gap-5">
        {data.map((d) => (
          <div key={d.label} className="flex flex-1 flex-col items-center gap-2">
            <div className="flex h-48 w-full max-w-[64px] flex-col-reverse overflow-hidden rounded-2xl border border-border">
              {segments.map((s) => (
                <div key={s.key} className={cn(s.color)} style={{ height: `${d.values[s.key] || 0}%` }} title={`${s.label}: ${d.values[s.key] || 0}%`} />
              ))}
            </div>
            <span className="text-xs font-bold text-muted-foreground">{d.label}</span>
          </div>
        ))}
      </div>
      <Legend className="mt-5" items={segments.map((s) => ({ color: s.color, label: s.label }))} />
    </div>
  );
}

/* ------------------------------------ Legend ------------------------------------- */
export function Legend({ items = [], className }) {
  return (
    <div className={cn("flex flex-wrap gap-x-5 gap-y-2", className)}>
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <span className={cn("h-3 w-3 rounded-full", it.color)} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* --------------------------------- Timeline rail --------------------------------- */
export function TimelineRail({ items = [] }) {
  return (
    <ol className="relative ml-3 space-y-5 border-l-2 border-border pl-7">
      {items.map((it) => {
        const t = TONES[it.tone || "orange"];
        return (
          <li key={it.title} className="relative">
            <span className={cn("absolute -left-[42px] flex h-9 w-9 items-center justify-center rounded-full ring-4 ring-background", t.tint, t.text)}>
              {it.icon && <it.icon size={18} weight="fill" />}
            </span>
            <div className="rounded-3xl border border-border bg-card p-4 shadow-soft">
              <p className={cn("text-xs font-bold uppercase tracking-wider", t.text)}>{it.label}</p>
              <p className="font-display font-extrabold">{it.title}</p>
              {it.desc && <p className="text-sm font-semibold text-muted-foreground">{it.desc}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ------------------------------------- Pill -------------------------------------- */
export function Pill({ children, tone = "neutral", icon: Icon }) {
  const t = TONES[tone];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold", t.tint, t.text)}>
      {Icon && <Icon size={15} weight="bold" />}
      {children}
    </span>
  );
}
